// JornadaBanda.jsx
//
// #1887 · la banda de /turno de la jornada de 12 h con botón (spec `.claude/specs/jornada-suelta/`, T24).
// Dirección B APROBADA (Sebastián, 30-oct-2026: «Apruebo · mockup: B»): una TARJETA con la hora grande cuando hay
// algo que activar y una BANDA delgada en los demás estados. Mockup: `tools/mockups/jornada_boton/hoja_direccion_B_*.png`
// y `eleccion_tipo_*.png` (R75). Sale del prototipo `mock/jornada-boton` sin su `localStorage.mock_jornada_*`.
//
// Estados (los decide `estadoDe`, que compara instantes; todo lo demás lo dice el backend):
//   1 antes       tarjeta con la hora de término que tendría, «Activar hasta las HH:MM» y «Ahora no» (R58, R67)
//   3 activo      banda delgada con la hora hasta la que se puede generar y usar la IA (R59)
//   4 por_terminar  a 30 min del término (R57)
//   5 terminado   con saldo: solo «Activar otro hasta las HH:MM», una por opción de `opciones_activar` (R70)
//   6 sin_saldo   sin IA: comprar una suelta o subir de plan (R60, R61)
// El chip «Comprar jornada» está en TODOS si `GET /me/suelta/` dice `disponible: true` (R71).
//
// NO vive en el navbar (`DxNavbar.js` no se toca: #1605-barra está bloqueado justo porque el indicador no cabe ahí):
// va en el flujo de la página, sobre el cockpit. El cockpit de escritorio mide su alto contra su `top` y solo vuelve a
// medir con `resize`: si la banda cambia de alto (tarjeta → banda, aviso que aparece) sin avisar, el cockpit queda más
// largo que la ventana. Por eso el `ResizeObserver` de abajo dispara un `resize` (R69; el test falla si se lo saca).
// La confirmación es un diálogo GLOBAL (`JornadaConfirmarDialog`, montado en App.js): también la abre un 403.
// Cubre R57, R58, R59, R60, R61, R67, R69, R70, R71, R75 — .claude/specs/jornada-suelta/
import { useLayoutEffect, useRef, useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import useJornadaEstado, { useAgotadasAbiertas } from "../../hooks/useJornadaEstado";
import CheckoutDialog from "../checkout/CheckoutDialog";
import {
  abrirCompraSuelta,
  abrirConfirmacionJornada,
  cuando,
  diaMes,
  estadoDe,
  guardadas,
  horasDe,
  hora,
  preciosTxt,
  opcionesDe,
  saldoDe,
  textoSaldo,
} from "../../helpers/suelta-api";
import { FUENTE, chipSx, ghostSx, primarySx } from "./estilos";

const TONOS = {
  neutro: { bg: "var(--bg-elevated)", borde: "rgba(var(--fg-rgb),0.1)", ico: "var(--accent-onboarding)" },
  primario: { bg: "rgba(var(--color-primary-rgb),0.1)", borde: "rgba(var(--color-primary-rgb),0.45)", ico: "var(--accent-onboarding)" },
  ok: { bg: "var(--bg-elevated)", borde: "rgba(var(--fg-rgb),0.1)", ico: "var(--color-success)" },
  aviso: { bg: "rgba(var(--color-warning-rgb),0.1)", borde: "rgba(var(--color-warning-rgb),0.4)", ico: "var(--color-warning)" },
};

// Banda delgada: una fila (estados 3, 4, 5, 6 y el 1 tras «Ahora no»).
const Fila = ({ tono, Icono, testId, children, acciones, mobile, arriba = false }) => (
  <Box
    data-testid={testId}
    role="status"
    sx={{
      display: "flex",
      alignItems: arriba ? "flex-start" : "center",
      gap: 1,
      px: mobile ? 1.25 : 1.5,
      py: mobile ? 0.75 : 0.625,
      minHeight: mobile ? 36 : 44,
      borderRadius: "12px",
      border: `1px solid ${TONOS[tono].borde}`,
      backgroundColor: TONOS[tono].bg,
      boxSizing: "border-box",
      minWidth: 0,
    }}
  >
    <Icono sx={{ fontSize: 18, color: TONOS[tono].ico, flexShrink: 0 }} />
    <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
    {acciones ? (
      <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0, alignItems: "center" }}>
        {acciones}
      </Stack>
    ) : null}
  </Box>
);

const Txt = ({ fuerte, children, mobile, testId }) => (
  <Typography
    component="div"
    data-testid={testId}
    sx={{
      fontFamily: FUENTE,
      fontSize: fuerte ? 13 : mobile ? 11.5 : 13,
      lineHeight: 1.3,
      fontWeight: fuerte ? 600 : 400,
      color: fuerte ? "var(--text-primary)" : "var(--text-muted)",
    }}
  >
    {children}
  </Typography>
);

const Fuerte = ({ children }) => (
  <Box component="span" sx={{ fontWeight: 600, color: "var(--text-primary)" }}>
    {children}
  </Box>
);

// Dirección B: tarjeta cuando el médico tiene que decidir algo.
const Tarjeta = ({ testId, hero, heroPie, heroNodo, titulo, cuerpo, pie, acciones, mobile, chip }) => (
  <Box
    data-testid={testId}
    role="status"
    sx={{
      display: "flex",
      flexDirection: mobile ? "column" : "row",
      alignItems: mobile ? "stretch" : "center",
      gap: mobile ? 0.75 : 2,
      px: mobile ? 1.5 : 2,
      py: 1.25,
      borderRadius: "14px",
      border: `1px solid ${TONOS.primario.borde}`,
      backgroundColor: TONOS.primario.bg,
      boxSizing: "border-box",
      minWidth: 0,
    }}
  >
    {mobile ? (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography sx={{ flex: 1, minWidth: 0, fontFamily: FUENTE, fontSize: 15, fontWeight: 700, color: "var(--text-primary)", lineHeight: 1.25 }}>{titulo}</Typography>
        {chip}
      </Box>
    ) : null}
    {heroNodo || (
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          flexDirection: mobile ? "row" : "column",
          alignItems: mobile ? "baseline" : "flex-start",
          gap: mobile ? 0.75 : 0,
          pr: mobile ? 0 : 2,
          borderRight: mobile ? "none" : "1px solid rgba(var(--fg-rgb),0.12)",
        }}
      >
        <Typography sx={{ fontFamily: FUENTE, fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", lineHeight: 1.2 }}>
          {mobile ? "Documentos e IA hasta las" : "hasta las"}
        </Typography>
        <Typography
          data-testid="jornada-hora"
          sx={{ fontFamily: FUENTE, fontSize: mobile ? 26 : 30, fontWeight: 700, color: "var(--text-primary)", lineHeight: 1.25, fontVariantNumeric: "tabular-nums" }}
        >
          {hero}
        </Typography>
        <Typography sx={{ fontFamily: FUENTE, fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.2 }}>{heroPie}</Typography>
      </Box>
    )}
    <Box sx={{ flex: 1, minWidth: 0 }}>
      {mobile ? null : (
        <Typography sx={{ fontFamily: FUENTE, fontSize: 15, fontWeight: 700, color: "var(--text-primary)", lineHeight: 1.25 }}>{titulo}</Typography>
      )}
      {mobile ? null : (
        <Typography sx={{ fontFamily: FUENTE, fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.35, mt: 0.25 }}>{cuerpo}</Typography>
      )}
      <Typography sx={{ fontFamily: FUENTE, fontSize: 12, color: "var(--text-muted)", lineHeight: 1.35, mt: mobile ? 0 : 0.25 }}>{pie}</Typography>
    </Box>
    {mobile ? null : chip}
    <Stack direction={mobile ? "row" : "column"} spacing={0.5} sx={{ flexShrink: 0, alignItems: "stretch", "& > button": mobile ? { flex: 1 } : {} }}>
      {acciones}
    </Stack>
  </Box>
);

const JornadaBanda = ({ mobile = false, sx = {} }) => {
  const { jornada, oferta, ahora } = useJornadaEstado();
  const agotadasAbiertas = useAgotadasAbiertas(); // detrás de `jornadas-agotadas` la banda no repite sus botones
  const [colapsada, setColapsada] = useState(false); // R67: «Ahora no». Vive solo en memoria: al recargar vuelve la tarjeta.
  const [eligiendoPlan, setEligiendoPlan] = useState(false);
  const raiz = useRef(null);

  // R69. Ver el comentario de la cabecera.
  useLayoutEffect(() => {
    const el = raiz.current;
    if (!el || mobile || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => window.dispatchEvent(new Event("resize")));
    ro.observe(el);
    return () => ro.disconnect();
  }, [mobile, jornada]);

  const estado = estadoDe(jornada, ahora);
  if (!estado) return null;

  const opciones = opcionesDe(jornada);
  const varias = opciones.length > 1;
  const op = opciones[0] || null;
  const sd = saldoDe(jornada);
  const saldoTxt = textoSaldo(jornada);
  const termina = op?.termina_si_activas || jornada.termina_si_activas;
  const esPlan = op?.opcion === "plan";

  const botonActivar = (texto, opcion) => (
    <Button data-testid="jornada-boton" onClick={() => abrirConfirmacionJornada(opcion)} sx={primarySx}>
      {texto}
    </Button>
  );

  // R71: el chip lo decide el backend (`disponible` es falso con la venta apagada, exentos, sin tope, a medida y durante la prueba).
  const puedeComprar = Boolean(oferta?.disponible) && !agotadasAbiertas;
  const chip = puedeComprar ? (
    <Button data-testid="suelta-comprar" onClick={abrirCompraSuelta} startIcon={<AddCircleOutlineIcon />} sx={chipSx(mobile)}>
      Jornada adicional
    </Button>
  ) : null;
  // Celular: la hora va sola en su línea y el chip baja a una segunda, junto al saldo (no cabe al lado).
  const pieCelular = (boton = null) =>
    chip || boton ? (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.75, mt: 0.5 }}>
        {boton ? null : (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Txt mobile>{saldoTxt}</Txt>
          </Box>
        )}
        {chip}
        {boton}
      </Box>
    ) : null;
  const comprar = puedeComprar ? (
    <Button data-testid="suelta-comprar" onClick={abrirCompraSuelta} sx={primarySx}>
      Sumar jornada adicional
    </Button>
  ) : null;
  const subir = agotadasAbiertas ? null : (
    <Button data-testid="jornada-subir-plan" onClick={() => setEligiendoPlan(true)} sx={comprar ? ghostSx : primarySx}>
      Subir de plan
    </Button>
  );
  // Precios SIEMPRE del backend (D15): nunca los del mockup.
  const precio = oferta?.disponible ? preciosTxt(oferta) : "";
  const diaRenueva = jornada?.periodo?.fin
    ? diaMes(new Date(new Date(`${jornada.periodo.fin}T12:00:00-03:00`).getTime() + 24 * 3600 * 1000).toISOString().slice(0, 10))
    : "";

  // Con dos tipos guardados la tarjeta muestra el saldo de cada uno; la hora de cada tipo se ve al elegir (R75).
  const celdaSaldo = (n, tipo) => (
    <Box key={tipo} data-testid={`jornada-saldo-${tipo}`} sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
      <Typography sx={{ fontFamily: FUENTE, fontSize: mobile ? 24 : 28, fontWeight: 700, color: "var(--text-primary)", lineHeight: 1.25, fontVariantNumeric: "tabular-nums" }}>{n}</Typography>
      <Typography sx={{ fontFamily: FUENTE, fontSize: 12.5, fontWeight: 600, color: "var(--text-secondary)", lineHeight: 1.2 }}>{`de ${tipo.replace("h", "\u00a0h")}`}</Typography>
    </Box>
  );
  const heroSaldo = varias ? (
    <Box
      sx={{
        flexShrink: 0,
        display: "flex",
        flexDirection: mobile ? "row" : "column",
        alignItems: mobile ? "baseline" : "flex-start",
        gap: mobile ? 1.5 : 0.25,
        pr: mobile ? 0 : 2,
        borderRight: mobile ? "none" : "1px solid rgba(var(--fg-rgb),0.12)",
      }}
    >
      <Typography sx={{ fontFamily: FUENTE, fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", lineHeight: 1.2 }}>Guardadas</Typography>
      {sd.n12 ? celdaSaldo(sd.n12, "12h") : null}
      {sd.n24 ? celdaSaldo(sd.n24, "24h") : null}
    </Box>
  ) : null;

  const horasTxt = horasDe(op?.opcion);
  // Pie de la tarjeta: lo que pasa si no se activa. Con el plan: el primer documento abre una de 24 h (descuenta 2) solo si
  // quedan 2 jornadas de 12 h; con 1, el backend responde `requiere_boton` y no se puede generar sin activar (R49).
  const pieAntes = (() => {
    if (varias) return `${sd.plan > 0 ? "Tu plan de este mes ya se usó." : ""}${mobile ? " Al activar eliges cuál y ves su hora de término." : ""}`.trim();
    if (esPlan) {
      return sd.delPlan >= 2
        ? `${saldoTxt} este mes. Sin activar, tu primer documento abre una jornada de 24\u00a0h y descuenta 2.`
        : `${saldoTxt} este mes. Para generar documentos tienes que activarla.`;
    }
    return `${sd.plan > 0 ? "Tu plan de este mes ya se usó. " : ""}${saldoTxt}. Se activa con el botón.`;
  })();

  let contenido = null;

  if (estado === "antes" && !colapsada) {
    contenido = (
      <Tarjeta
        testId="jornada-tarjeta"
        mobile={mobile}
        heroNodo={heroSaldo}
        hero={termina ? hora(termina) : ""}
        heroPie={termina ? cuando(termina, ahora) : ""}
        titulo={varias ? "Activa una jornada" : mobile ? `Jornada de ${horasTxt} horas` : `Activa tu jornada de ${horasTxt} horas`}
        cuerpo={
          varias
            ? "Al activar eliges cuál y ves hasta qué hora puedes generar documentos y usar la IA."
            : "Hasta esa hora puedes generar documentos y usar la IA."
        }
        pie={pieAntes}
        chip={chip}
        acciones={
          <>
            {varias
              ? botonActivar(mobile ? "Activar" : "Activar una jornada")
              : botonActivar(mobile ? "Activar" : `Activar hasta las ${hora(termina)}`, op?.opcion)}
            <Button data-testid="jornada-ahora-no" onClick={() => setColapsada(true)} sx={{ ...ghostSx, border: "none", color: "var(--text-muted)" }}>
              Ahora no
            </Button>
          </>
        }
      />
    );
  } else if (estado === "antes") {
    // R67: tras «Ahora no» queda la banda delgada con el botón a mano.
    contenido = (
      <Fila
        tono="primario"
        Icono={AccessTimeOutlinedIcon}
        testId="jornada-banda-antes"
        mobile={mobile}
        arriba={mobile}
        acciones={
          mobile ? null : (
            <>
              {chip}
              {varias ? botonActivar("Activar una jornada") : botonActivar(`Activar jornada de ${horasTxt}\u00a0h`, op?.opcion)}
            </>
          )
        }
      >
        {mobile ? (
          <>
            <Txt fuerte testId="jornada-hora">
              {varias ? "Tienes jornadas guardadas" : `Jornada de ${horasTxt}\u00a0h, hasta las ${hora(termina)} ${cuando(termina, ahora)}`}
            </Txt>
            <Txt mobile>{`${saldoTxt}.${esPlan && sd.delPlan >= 2 ? " Sin activar, tu primer documento abre una jornada de 24\u00a0h y descuenta 2." : ""}`}</Txt>
            {pieCelular(varias ? botonActivar("Activar") : botonActivar("Activar", op?.opcion))}
          </>
        ) : varias ? (
          <Txt>
            <Fuerte>Tienes jornadas guardadas: </Fuerte>
            {`elige cuál activar y ves su hora de término. ${saldoTxt}.`}
          </Txt>
        ) : (
          <Txt>
            <Fuerte>{`Jornada de ${horasTxt}\u00a0h: `}</Fuerte>
            {`genera documentos y usa la IA `}
            <Fuerte>{`hasta las ${hora(termina)} ${cuando(termina, ahora)}`}</Fuerte>
            {`. ${saldoTxt}.${esPlan && sd.delPlan >= 2 ? " Sin activar, el primer documento abre una de 24\u00a0h." : ""}`}
          </Txt>
        )}
      </Fila>
    );
  } else if (estado === "activo") {
    const quien = jornada.tipo === "24h" || jornada.tipo === "entera" ? "Jornada de 24\u00a0h activa." : "Jornada de 12\u00a0h activa.";
    contenido = (
      <Fila tono="ok" Icono={CheckCircleOutlineIcon} testId="jornada-activa" mobile={mobile} arriba={mobile && Boolean(chip)} acciones={!mobile ? chip : null}>
        {mobile ? (
          <>
            <Txt fuerte testId="jornada-hora">{`Documentos e IA hasta las ${hora(jornada.cierra_en)} ${cuando(jornada.cierra_en, ahora)}`}</Txt>
            {pieCelular()}
          </>
        ) : (
          <Txt>
            <Fuerte>{quien}</Fuerte>
            {` Puedes generar documentos y usar la IA `}
            <Fuerte>{`hasta las ${hora(jornada.cierra_en)} ${cuando(jornada.cierra_en, ahora)}`}</Fuerte>.
          </Txt>
        )}
      </Fila>
    );
  } else if (estado === "por_terminar") {
    const min = Math.max(Math.ceil((new Date(jornada.cierra_en) - ahora) / 60000), 0);
    contenido = (
      <Fila tono="aviso" Icono={ErrorOutlineIcon} testId="jornada-aviso-termino" mobile={mobile} arriba={mobile && Boolean(chip)} acciones={mobile ? null : chip}>
        {mobile ? (
          <>
            <Txt fuerte testId="jornada-hora">{`Tu jornada termina a las ${hora(jornada.cierra_en)}: quedan ${min} min`}</Txt>
            <Txt mobile>Después puedes leer, copiar y editar, pero sin IA.</Txt>
            {pieCelular()}
          </>
        ) : (
          <Txt>
            <Fuerte>{`Tu jornada termina a las ${hora(jornada.cierra_en)}: quedan ${min} min.`}</Fuerte>
            {` Después puedes seguir leyendo, copiando, editando y guardando, pero sin IA.`}
          </Txt>
        )}
      </Fila>
    );
  } else if (estado === "terminado") {
    // R70: SOLO lo que se activa con el botón (`opciones_activar`); jamás una opción para abrir una de 24 h del plan.
    const titulo = jornada.ultimo_cierre ? `Tu jornada terminó a las ${hora(jornada.ultimo_cierre)}` : "Tu jornada terminó";
    const unica = opciones.length === 1 && opciones[0].opcion === "plan";
    const botonOtra = (o) => (
      <Button key={o.opcion} data-testid="jornada-activar-otra" data-opcion={o.opcion} onClick={() => abrirConfirmacionJornada(o.opcion)} sx={primarySx}>
        {unica
          ? mobile
            ? "Activar otro"
            : `Activar otro hasta las ${hora(o.termina_si_activas)}`
          : mobile
            ? `Activar ${horasDe(o.opcion)}\u00a0h · ${hora(o.termina_si_activas)}`
            : `Activar de ${horasDe(o.opcion)}\u00a0h hasta las ${hora(o.termina_si_activas)}`}
      </Button>
    );
    contenido = (
      <Fila
        tono="neutro"
        Icono={MenuBookOutlinedIcon}
        testId="jornada-sin-ia"
        mobile={mobile}
        arriba={mobile}
        acciones={
          mobile ? null : (
            <>
              {chip}
              {opciones.map(botonOtra)}
            </>
          )
        }
      >
        {mobile ? (
          <>
            <Txt fuerte testId="jornada-hora">{titulo}</Txt>
            <Txt mobile>{`Puedes leer, copiar, editar y guardar, sin IA. ${saldoTxt}.`}</Txt>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", flexWrap: "wrap", gap: 0.75, mt: 0.5 }}>
              {chip}
              {opciones.map(botonOtra)}
            </Box>
          </>
        ) : (
          <Txt>
            <Fuerte>{`${titulo}.`}</Fuerte>
            {` Puedes seguir leyendo, copiando, editando y guardando, pero sin IA. ${saldoTxt}.`}
          </Txt>
        )}
      </Fila>
    );
  } else if (estado === "sin_saldo") {
    const lectura = "Puedes seguir leyendo, copiando, editando y guardando, pero sin IA.";
    const renueva = diaRenueva && jornada?.periodo?.incluidas ? `Se renuevan el ${diaRenueva}.` : "";
    const guardadasTxt = sd.hayGuardadas ? ` Guardadas: ${guardadas(sd.n12, sd.n24)}.` : "";
    contenido = (
      <Fila
        tono="aviso"
        Icono={ErrorOutlineIcon}
        testId="jornada-sin-ia"
        mobile={mobile}
        acciones={
          mobile ? null : (
            <>
              {comprar}
              {subir}
            </>
          )
        }
      >
        <Txt fuerte>{jornada?.periodo?.incluidas ? "Se acabaron tus jornadas de este mes." : "No tienes jornadas para generar con IA."}</Txt>
        <Txt mobile={mobile}>{mobile ? `Puedes leer, copiar, editar y guardar, sin IA. ${renueva}${guardadasTxt}` : `${lectura} ${renueva}${guardadasTxt}`}</Txt>
        {precio && !mobile ? <Txt mobile={mobile}>{`Jornada adicional, sin cambiar de plan: ${precio}.`}</Txt> : null}
        {mobile ? (
          <>
            {precio ? <Txt mobile>{`Jornada adicional, sin cambiar de plan: ${precio}.`}</Txt> : null}
            {/* En columna: «Sumar jornada adicional» y «Subir de plan» no caben lado a lado en 390 px (el texto se cortaba). */}
            {comprar || subir ? (
              <Stack direction="column" spacing={0.75} sx={{ mt: 0.75, "& > button": { width: "100%" } }}>
                {comprar}
                {subir}
              </Stack>
            ) : null}
          </>
        ) : null}
      </Fila>
    );
  }

  return (
    <Box ref={raiz} data-testid="jornada-banda" data-estado={estado} sx={{ fontFamily: FUENTE, boxSizing: "border-box", ...sx }}>
      {contenido}
      {eligiendoPlan ? <CheckoutDialog open onClose={() => setEligiendoPlan(false)} /> : null}
    </Box>
  );
};

export default JornadaBanda;
